import {
  ConfigActionExecuteFaaS,
  ConfigActionSendMessage,
  ConfigActionTransferToAgent,
  ConfigActionTransferToSkill,
  NextActionExecutionHistory,
  NextActionExecutionHistoryItem,
  PolicyExecutionStatus,
} from '@liveperson/lpcontextschemalib';
import { PolicyDebugProp, PolicyEvalDisposition, TimeSeries } from '../../enums';
import NextActionSummaryCard from './next-action-summary-card';
import NextActionExecutionList from './next-action-execution-list';
import { dateTimeFormatter } from './util/formatters';
import { policyActionResultTitleMapper, policyEvalResultToTestIdMapper } from './util/mappers';
import {
  policyActionResultParser, singlePolicyActionParser, parseConditionToWords, markupValueParser,
} from './util/parsers';
import PolicySearch, { policyDebugPropOptions, timeSeriesOptions } from './policy-search';
import PolicyExecution from './policy-execution';
import PolicyVariablesTable from './policy-variables-table';
import {
  PolicyExecutionTableHeaders,
  PolicyCondition,
  PolicyAction,
  PolicyResultComponent,
} from './policy-execution-table';
import PolicyExecutionHistory from './policy-execution-history';
import { lookupExceptionStr, UNDEFINED_POLICY_VARIABLE_DISPLAY } from './util/helpers';

interface PolicyDebugPropOption {
  value: PolicyDebugProp;
  label: string;
}

interface TimeSeriesOption {
  value: TimeSeries;
  label: string;
}

interface PolicySearchProps {
  term: string;
  prop: PolicyDebugProp;
  startDate: Date;
  endDate: Date;
}

export const POLICY_EXECUTION_TABLE_COLUMN_WIDTHS = ['15%', '15%', '50%', '15%', '5%'];

export interface PolicyVariablesProps {
  variables?: Array<string>;
  valueMap?: { [key: string]: string };
  exceptionsMap?: { [key: string]: string };
}

export interface PolicyActionDisplay {
  title: string;
  message: string;
}

interface PolicySearchComponentProps {
  setSelectedPolicyDebugPropOption: (opt: PolicyDebugPropOption) => void;
  setSearchTerm: (term: string) => void;
  setSelectedTimeSeriesOption: (opt: TimeSeriesOption) => void;
  onChange: (props: PolicySearchProps) => void;
}

interface PolicyResultProps {
  policyEvalDisposition: PolicyEvalDisposition;
  order?: number;
  isEmphasizedPolicyResult: boolean;
}

interface PolicyExecutionProps {
  policyRun: NextActionExecutionHistoryItem;
  policyVariables?: { [key: string]: any };
  policyExceptions?: { [key: string]: any };
  isFirst: boolean;
  isLast: boolean;
  transientVariables?: { [key: string]: any };
  measure: () => void;
}

interface PolicyConditionProps {
  condition?: string;
  variables?: Array<string>;
  transientVariables?: { [key: string]: any };
  valueMap?: { [key: string]: string };
  exceptionsMap?: { [key: string]: string };
  isEmphasizedPolicyResult: boolean;
}

interface PolicyActionProps {
  isEmphasizedPolicyResult: boolean;
  actions?: PolicyExecutionStatus | Array<ConfigActionSendMessage
  | ConfigActionTransferToAgent | ConfigActionTransferToSkill | ConfigActionExecuteFaaS>;
}

interface ExecutionCardProps {
  execution: NextActionExecutionHistory;
  isDrawerOpen: boolean;
}

interface SearchTermCacheItem {
  lastUsed: Date;
  term: string;
}

type SearchTermCache = {
  [key: string]: Array<SearchTermCacheItem>;
};

interface PaginatedResultSet {
  limit: number;
  page: number;
  totalDocs: number;
  totalPages: number;
  pagingCounter: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
  docs: Array<any>;
}

interface PolicyExecutionHistoryPaginatedResultSet extends PaginatedResultSet {
  docs: Array<NextActionExecutionHistory>;
}

export {
  NextActionSummaryCard,
  PolicySearch,
  PolicyResultComponent,
  PolicyExecution,
  PolicyVariablesTable,
  PolicyExecutionTableHeaders,
  PolicyCondition,
  PolicyAction,
  PolicyExecutionHistory,
  policyDebugPropOptions,
  timeSeriesOptions,
  lookupExceptionStr,
  dateTimeFormatter,
  policyActionResultParser,
  singlePolicyActionParser,
  parseConditionToWords,
  policyEvalResultToTestIdMapper,
  policyActionResultTitleMapper,
  UNDEFINED_POLICY_VARIABLE_DISPLAY,
  markupValueParser,
  NextActionExecutionList,
};

export type {
  PolicyResultProps,
  PolicySearchProps,
  PolicyDebugPropOption,
  TimeSeriesOption,
  PolicySearchComponentProps,
  SearchTermCacheItem,
  SearchTermCache,
  PolicyExecutionProps,
  PolicyConditionProps,
  ExecutionCardProps,
  PolicyActionProps,
  PaginatedResultSet,
  PolicyExecutionHistoryPaginatedResultSet,
};
